import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AlertCircle, ArrowLeft, ChevronDown, ChevronRight, FileText, Loader2, Upload, Video } from 'lucide-react';
import AccessTokenManager from '../../services/accessTokenManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface LibraryEntry {
  timestampSec: number;
  anchoredBy: string[];
  photoAnalysis: any;
}

interface WorkItem {
  trade: string;
  description: string;
  location: string;
  confidence: 'high' | 'medium' | 'low';
  quantity?: number | null;
  unit?: string | null;
  dimensionCues?: Array<{ observation: string; timestampSec?: number; confidence: string }>;
  existingCondition?: { material?: string | null; condition?: string | null; notes?: string | null };
  adjacentLabor?: string[];
  evidenceTimestamps?: number[];
}

interface Job {
  name: string;
  category: string;
  rationale: string;
  workItems: WorkItem[];
}

interface VideoScope {
  propertyType?: string;
  videoQuality?: string;
  confidence?: number;
  roomsIdentified?: string[];
  sceneIndex?: Array<{ startSec: number; endSec: number; location: string; focus: string; visibleItems: string[] }>;
  jobs?: Job[];
  notes?: string[];
  summary?: string;
}

interface VideoIntakeResult {
  status: string;
  transcript: string;
  scope: VideoScope;
  library: LibraryEntry[];
  meta: { videoBytes: number; transcriptLength: number; framesExtracted: number; librarySize: number };
}

const confidenceBadgeClass = (c: string) => {
  if (c === 'high') return 'bg-green-100 text-green-800';
  if (c === 'medium') return 'bg-yellow-100 text-yellow-800';
  return 'bg-gray-100 text-gray-600';
};

export const VideoIntakeReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<VideoIntakeResult | null>(null);
  const [expandedLibrary, setExpandedLibrary] = useState<Set<number>>(new Set());
  const [expandedWorkItem, setExpandedWorkItem] = useState<Set<string>>(new Set());

  const handleAnalyze = async () => {
    if (!file) {
      setError('Please choose a video file');
      return;
    }

    setProcessing(true);
    setError(null);
    setResult(null);
    setElapsedSec(0);

    const startedAt = Date.now();
    const tick = setInterval(() => {
      setElapsedSec(Math.round((Date.now() - startedAt) / 1000));
    }, 1000);

    try {
      const token = AccessTokenManager.getAccessToken();
      if (!token) throw new Error('No auth token available');

      const formData = new FormData();
      formData.append('video', file);

      const response = await fetch(`${API_BASE_URL}/scope/video-intake`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data: VideoIntakeResult = await response.json();
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      clearInterval(tick);
      setProcessing(false);
    }
  };

  const toggleLibraryEntry = (idx: number) => {
    setExpandedLibrary(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleWorkItem = (key: string) => {
    setExpandedWorkItem(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="container mx-auto px-4 py-4 sm:py-8 max-w-6xl">
      <div className="mb-4 sm:mb-6 flex items-center gap-3">
        <Button variant="outline" onClick={() => navigate('/sow')} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to projects
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Create from video</h1>
          <p className="text-sm text-gray-600 mt-1">
            Record a walkthrough; we'll generate an initial scope and a reference frame library.
          </p>
        </div>
      </div>

      {!result && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Video className="h-5 w-5" />
              Upload walkthrough video
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs sm:text-sm text-gray-600">
              ≤5 minutes, 1080p, under ~150 MB recommended. Processing usually takes 2-5 minutes.
            </p>

            <input
              type="file"
              accept="video/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              disabled={processing}
            />
            {file && (
              <p className="text-xs text-gray-500">
                {file.name} — {(file.size / 1024 / 1024).toFixed(1)} MB
              </p>
            )}

            <Button onClick={handleAnalyze} disabled={processing || !file} className="gap-2">
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing... {elapsedSec}s
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Analyze video
                </>
              )}
            </Button>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 text-red-800 border border-red-200">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm break-words">{error}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {result && (
        <div className="space-y-6">
          {/* Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="text-gray-800">{result.scope.summary}</p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-gray-600">
                <span><strong>Rooms:</strong> {(result.scope.roomsIdentified || []).join(', ') || '—'}</span>
                <span><strong>Confidence:</strong> {result.scope.confidence ?? '—'}/10</span>
                <span><strong>Video quality:</strong> {result.scope.videoQuality ?? '—'}</span>
                <span><strong>Frames extracted:</strong> {result.meta.framesExtracted}</span>
                <span><strong>Library size:</strong> {result.meta.librarySize}</span>
              </div>
              {result.transcript && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-600 hover:text-gray-900">Show transcript</summary>
                  <p className="mt-2 p-3 bg-gray-50 rounded text-gray-700 whitespace-pre-wrap">{result.transcript}</p>
                </details>
              )}
            </CardContent>
          </Card>

          {/* Library */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Reference library ({result.library.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {result.library.map((entry, idx) => {
                  const expanded = expandedLibrary.has(idx);
                  return (
                    <div key={idx} className="border border-gray-200 rounded-md bg-white p-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 leading-tight">
                          Frame @ {entry.timestampSec.toFixed(1)}s
                        </h3>
                      </div>
                      {entry.anchoredBy && entry.anchoredBy.length > 0 && (
                        <div className="space-y-0.5">
                          <p className="text-[10px] uppercase tracking-wide text-gray-500">Anchored to</p>
                          <ul className="text-xs text-gray-700 list-disc ml-4">
                            {entry.anchoredBy.map((reason, i) => (
                              <li key={i}>{reason}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {entry.photoAnalysis?.description && (
                        <p className="text-xs text-gray-600 leading-snug">{entry.photoAnalysis.description}</p>
                      )}
                      <button
                        onClick={() => toggleLibraryEntry(idx)}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        {expanded ? 'Hide details' : 'Show details'}
                      </button>
                      {expanded && (
                        <pre className="text-[10px] bg-gray-900 text-gray-100 p-2 rounded overflow-x-auto max-h-64">
                          {JSON.stringify(entry.photoAnalysis, null, 2)}
                        </pre>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Scope: Jobs & Work Items */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Initial scope</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(result.scope.jobs || []).map((job, jIdx) => (
                <div key={jIdx} className="border border-gray-200 rounded-md">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900">{job.name}</h3>
                    <p className="text-xs text-gray-600 mt-1">{job.rationale}</p>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {job.workItems.map((wi, wIdx) => {
                      const key = `${jIdx}-${wIdx}`;
                      const expanded = expandedWorkItem.has(key);
                      return (
                        <div key={wIdx} className="px-4 py-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[10px] px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 uppercase">{wi.trade}</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded ${confidenceBadgeClass(wi.confidence)}`}>
                                  {wi.confidence}
                                </span>
                                {wi.quantity != null && (
                                  <span className="text-xs font-semibold text-gray-900">
                                    {wi.quantity} {wi.unit}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-800 mt-1">{wi.description}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{wi.location}</p>
                            </div>
                            <button
                              onClick={() => toggleWorkItem(key)}
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 whitespace-nowrap mt-1"
                            >
                              {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                              {expanded ? 'Less' : 'More'}
                            </button>
                          </div>
                          {expanded && (
                            <div className="mt-3 pl-2 border-l-2 border-gray-200 space-y-2 text-xs">
                              {wi.existingCondition && (wi.existingCondition.material || wi.existingCondition.condition) && (
                                <div>
                                  <strong className="text-gray-700">Existing:</strong>{' '}
                                  <span className="text-gray-600">
                                    {wi.existingCondition.material}
                                    {wi.existingCondition.condition ? ` — ${wi.existingCondition.condition}` : ''}
                                    {wi.existingCondition.notes ? ` (${wi.existingCondition.notes})` : ''}
                                  </span>
                                </div>
                              )}
                              {wi.dimensionCues && wi.dimensionCues.length > 0 && (
                                <div>
                                  <strong className="text-gray-700">Dimension cues:</strong>
                                  <ul className="mt-1 ml-4 list-disc text-gray-600 space-y-0.5">
                                    {wi.dimensionCues.map((c, i) => (
                                      <li key={i}>
                                        {c.observation}
                                        {c.timestampSec != null && <span className="text-gray-400"> @{c.timestampSec}s</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {wi.adjacentLabor && wi.adjacentLabor.length > 0 && (
                                <div>
                                  <strong className="text-gray-700">Adjacent labor:</strong>
                                  <ul className="mt-1 ml-4 list-disc text-gray-600 space-y-0.5">
                                    {wi.adjacentLabor.map((l, i) => (
                                      <li key={i}>{l}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {wi.evidenceTimestamps && wi.evidenceTimestamps.length > 0 && (
                                <div>
                                  <strong className="text-gray-700">Evidence:</strong>{' '}
                                  <span className="text-gray-600">{wi.evidenceTimestamps.map(t => `${t}s`).join(', ')}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Notes */}
          {result.scope.notes && result.scope.notes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Notes for the estimator
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-700 list-disc ml-5">
                  {result.scope.notes.map((note, i) => (
                    <li key={i}>{note}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Reset for another try */}
          <div className="flex justify-between items-center pt-4">
            <Button variant="outline" onClick={() => { setResult(null); setFile(null); setError(null); }} className="gap-2">
              <Video className="h-4 w-4" />
              Analyze another video
            </Button>
            <p className="text-xs text-gray-500">
              Save-as-SOW coming in next iteration.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
